/**
 * The load state of one admin page, apart from React so that a test drives it without a DOM
 * (components/AdminPage.tsx reads it through `useSyncExternalStore`).
 *
 * Two rules live here:
 *
 * - `refresh` is what a button calls, and it is IGNORED while a request is in flight. The admin
 *   reads scan every run file on the server's event loop (docs/backlog.md, "Admin run scan"), so
 *   an impatient double click must not become two scans.
 * - `invalidate` is what a write calls when the list on screen can no longer be trusted. It is
 *   never dropped. If a request is in flight, that request may have been answered from before the
 *   write, so its answer is discarded and the load runs again.
 *
 * A reload keeps the data on screen until the new answer arrives, so that a refresh does not
 * blank a page somebody is reading.
 */
export type Loaded<T> =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; data: T };

export interface LoaderSnapshot<T> {
  state: Loaded<T>;
  inFlight: boolean;
}

export class Loader<T> {
  private snapshot: LoaderSnapshot<T> = { state: { status: 'loading' }, inFlight: false };
  private readonly listeners = new Set<() => void>();
  /** Bumped by `dispose`: an answer from an older generation is from a page that has gone. */
  private generation = 0;
  private stale = false;

  constructor(
    private readonly load: () => Promise<T>,
    private readonly describeError: (e: unknown) => string,
  ) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): LoaderSnapshot<T> => this.snapshot;

  private set(next: Partial<LoaderSnapshot<T>>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) listener();
  }

  /** A button's reload. Ignored while a request is in flight. Resolves when the load settles. */
  readonly refresh = (): Promise<void> => (this.snapshot.inFlight ? Promise.resolve() : this.run());

  /** A write's reload. Never dropped: an in-flight answer is discarded and the load runs again. */
  readonly invalidate = (): Promise<void> => {
    if (!this.snapshot.inFlight) return this.run();
    this.stale = true;
    return Promise.resolve();
  };

  readonly setData = (data: T): void => this.set({ state: { status: 'ready', data } });

  /** The page has gone. A late answer changes nothing, and a remount may load again at once. */
  readonly dispose = (): void => {
    this.generation++;
    this.stale = false;
    this.snapshot = { ...this.snapshot, inFlight: false };
  };

  private async run(): Promise<void> {
    const generation = this.generation;
    this.set({ inFlight: true });
    let next: Loaded<T>;
    try {
      next = { status: 'ready', data: await this.load() };
    } catch (e) {
      next = { status: 'failed', message: this.describeError(e) };
    }
    if (generation !== this.generation) return;
    if (this.stale) {
      this.stale = false;
      return this.run();
    }
    this.set({ state: next, inFlight: false });
  }
}
